import { useEffect, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { translations } from '@/i18n/translations';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { getSignedImageUrl } from '@/hooks/useSignedUrl';
import { SEO_LANGUAGE } from '@/utils/jsonLd';

interface PageSEOProps {
  /** Page-specific title suffix, e.g. "Gallery" */
  titleKey?: keyof import('@/i18n/translations').Translations;
  /** Fallback title if no translation key */
  title?: string;
  /** Page-specific description key */
  descriptionKey?: keyof import('@/i18n/translations').Translations;
  /** Fallback description */
  description?: string;
  /** Canonical path, e.g. "/gallery" */
  path?: string;
  /** JSON-LD structured data object or array (rendered as @graph) */
  jsonLd?: Record<string, any> | Record<string, any>[];
  /** Override OG image with a specific URL (e.g. instrument hero) */
  ogImage?: string;
}

const DOMAIN = 'https://szoradi.com';

const PageSEO = ({ titleKey, title, descriptionKey, description, path = '', jsonLd, ogImage }: PageSEOProps) => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();
  const [resolvedOgImage, setResolvedOgImage] = useState<string>('');

  // Resolve OG image: prop override > admin seo_og_image_path > static fallback
  useEffect(() => {
    if (ogImage) {
      setResolvedOgImage(ogImage);
      return;
    }

    if (settings.seo_og_image_path) {
      let cancelled = false;
      getSignedImageUrl(settings.seo_og_image_path, 'instrument-images').then(url => {
        if (!cancelled && url) setResolvedOgImage(url);
      });
      return () => { cancelled = true; };
    }

    setResolvedOgImage(`${DOMAIN}/og-image.jpg`);
  }, [ogImage, settings.seo_og_image_path]);

  useEffect(() => {
    // SEO output is locked to German (SEO_LANGUAGE) for crawler consistency
    const seoT = translations[SEO_LANGUAGE];
    const brandName = 'Szoradi Instruments';
    const adminTitle = settings.seo_title_de; // German-first
    const pageTitle = titleKey ? (seoT as any)[titleKey] : title;
    
    if (path === '' || path === '/') {
      document.title = adminTitle || seoT.metaTitle;
    } else {
      document.title = pageTitle ? `${pageTitle} | ${brandName}` : seoT.metaTitle;
    }

    // Meta description — locked to German
    const adminDesc = settings.seo_description_de;
    const pageDesc = descriptionKey ? (seoT as any)[descriptionKey] : description;
    const metaDesc = (path === '' || path === '/') 
      ? (adminDesc || seoT.metaDescription)
      : (pageDesc || seoT.metaDescription);
    
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', metaDesc);

    // Canonical
    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', `${DOMAIN}${path}`);

    // OG tags
    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };
    
    setMeta('meta[property="og:title"]', document.title);
    setMeta('meta[property="og:description"]', metaDesc);
    setMeta('meta[property="og:url"]', `${DOMAIN}${path}`);
    setMeta('meta[name="twitter:title"]', document.title);
    setMeta('meta[name="twitter:description"]', metaDesc);
    setMeta('meta[name="twitter:url"]', `${DOMAIN}${path}`);

    // OG image
    if (resolvedOgImage) {
      setMeta('meta[property="og:image"]', resolvedOgImage);
      setMeta('meta[name="twitter:image"]', resolvedOgImage);
    }

    // HTML lang — keep dynamic for UX
    document.documentElement.lang = language;

    // JSON-LD for this page (supports single object or array as @graph)
    let scriptEl = document.getElementById('page-jsonld');
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'page-jsonld';
        scriptEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptEl);
      }
      // If array, wrap as @graph; if already has @graph, use as-is
      const ldPayload = Array.isArray(jsonLd)
        ? { '@context': 'https://schema.org', '@graph': jsonLd }
        : jsonLd;
      scriptEl.textContent = JSON.stringify(ldPayload);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, [language, titleKey, title, descriptionKey, description, path, jsonLd, settings, resolvedOgImage]);

  return null;
};

export default PageSEO;
